/*eslint max-lines-per-function: ["error", 325]*/
import type { TFunction } from 'i18next';

import { contractForGrid } from '../../../pok/components/Contracts/ContractSearch';
import { GridRecord } from '../../components/Grid/GridDataTypes';
import {
  CreateUpdateContractDto,
  CreateUpdateContractDtoFromJSON,
  DefaultApi,
  GetContractDto,
  GetContractExtendedDto,
  GetLastEditorDto,
  GetSignResultsDto,
  SignaturePositionSchemaDto,
} from '../autogenerated/pokApiClient';
import { ContractStatusEnum } from '../validation/enums';
import {
  ContractSchema,
  ContractToWaitingToSignSchema,
} from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';
import { Option } from '../../../pok/components/Selects/Selector';
import { TKeys } from '../../hooks/useTranslation';

export interface ContractContextInterface {
  getWithAttachmentById: (id: string) => Promise<GetContractExtendedDto>;
  getAllByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    tv: boolean,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (
    tv: boolean,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getPendingByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    tv: boolean,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getPendingCount: (
    tv: boolean,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getWaitingForApprovalByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    tv: boolean,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getWaitingForApprovalCount: (
    tv: boolean,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getApprovedNotSentByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    tv: boolean,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getApprovedNotSentCount: (
    tv: boolean,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  getUnsignedByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    tv: boolean,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getUnsignedCount: (
    tv: boolean,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;

  getToSignByMeByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getToSignByMeCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getSignedByMeByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getSignedByMeCount: (
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getToUpdateByPortion: (
    t: TFunction<'projectLng', 'projectLng'>,
    tk: TKeys<'projectLng'>,
    contractId: string,
    tv: boolean,
    projectId: string,
    clientId: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getToUpdateCount: (
    contractId: string,
    tv: boolean,
    projectId?: string,
    clientId?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  update: (id: string, dto: CreateUpdateContractDto) => Promise<void>;
  create: (dto: CreateUpdateContractDto) => Promise<GetContractDto>;
  deactivate: (id: string) => Promise<void>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  sign: (selectedContractsIds?: string[]) => Promise<GetSignResultsDto>;
  unsign: (
    selectedContractsIds?: string[],
    reason?: string,
  ) => Promise<GetSignResultsDto>;
  waitingToSign: (id: string) => Promise<void>;
  markSentByHand: (id: string) => Promise<void>;
  markAsSignedContract: (id: string) => Promise<void>;
  markEnteredStatus: (id: string) => Promise<void>;
  saveSignature: (
    id: string,
    signaturePosition: SignaturePositionSchemaDto,
  ) => Promise<void>;
  updateAndRemove: (
    removeContractId: string,
    updatedContractId: string,
  ) => Promise<void>;
  getContractEmailsByClientId: (
    text: string,
    clientId?: string,
  ) => Promise<Option[]>;
}

export const ContractContext = (api: DefaultApi) => {
  return {
    getWithAttachmentById: (id: string) =>
      api.contractControllerGetWithAttachment(id),
    getAllByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      tv: boolean,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetAllByPortion(
        undefined,
        undefined,
        tv,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getAllCount: (tv: boolean, filterText?: string, filterColumns?: string) =>
      api.contractControllerGetAllCount(
        undefined,
        undefined,
        tv,
        filterText || '',
        filterColumns,
      ),
    getPendingByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      tv: boolean,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetAllByPortion(
        ContractStatusEnum.Entered,
        undefined,
        tv,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getPendingCount: (
      tv: boolean,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.contractControllerGetAllCount(
        ContractStatusEnum.Entered,
        undefined,
        tv,
        filterText || '',
        filterColumns,
      ),
    getWaitingForApprovalByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      tv: boolean,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetAllByPortion(
        ContractStatusEnum.WaitingForSigning,
        undefined,
        tv,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getWaitingForApprovalCount: async (
      tv: boolean,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.contractControllerGetAllCount(
        ContractStatusEnum.WaitingForSigning,
        undefined,
        tv,
        filterText || '',
        filterColumns,
      ),
    getApprovedNotSentByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      tv: boolean,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetAllByPortion(
        ContractStatusEnum.Signed,
        undefined,
        tv,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getApprovedNotSentCount: (
      tv: boolean,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.contractControllerGetAllCount(
        ContractStatusEnum.Signed,
        undefined,
        tv,
        filterText || '',
        filterColumns,
      ),
    getUnsignedByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      tv: boolean,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetAllByPortion(
        ContractStatusEnum.Unsigned,
        undefined,
        tv,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getUnsignedCount: (
      tv: boolean,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.contractControllerGetAllCount(
        ContractStatusEnum.Unsigned,
        undefined,
        tv,
        filterText || '',
        filterColumns,
      ),

    getToSignByMeByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetAllSignByMeByPortion(
        ContractStatusEnum.WaitingForSigning,
        undefined,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getToSignByMeCount: async (filterText?: string, filterColumns?: string) =>
      api.contractControllerGetAllSignByMeCount(
        ContractStatusEnum.WaitingForSigning,
        undefined,
        filterText || '',
        filterColumns,
      ),
    getSignedByMeByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetAllSignByMeByPortion(
        undefined,
        undefined,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getSignedByMeCount: (filterText?: string, filterColumns?: string) =>
      api.contractControllerGetAllSignByMeCount(
        undefined,
        undefined,
        filterText || '',
        filterColumns,
      ),
    getToUpdateByPortion: async (
      t: TFunction<'projectLng', 'projectLng'>,
      tk: TKeys<'projectLng'>,
      contractId: string,
      tv: boolean,
      projectId: string,
      clientId: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.contractControllerGetToUpdateByPortion(
        contractId,
        tv,
        projectId,
        clientId,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns,
      );
      return data.map(item => contractForGrid(t, tk, item));
    },
    getToUpdateCount: (
      contractId: string,
      tv: boolean,
      projectId?: string,
      clientId?: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.contractControllerGetToUpdateCount(
        contractId,
        tv,
        projectId,
        clientId,
        filterText,
        filterColumns,
      ),
    update: (id: string, dto: CreateUpdateContractDto) =>
      api.contractControllerUpdate(id, dto),
    create: (dto: CreateUpdateContractDto) => api.contractControllerCreate(dto),
    deactivate: (id: string) => api.contractControllerDeactivate(id),
    getLastEditor: (id: string) => api.contractControllerGetLastEditor(id),
    sign: (selectedContractsIds?: string[]) =>
      api.contractControllerSign({ selectedContractsIds }),
    unsign: (selectedContractsIds?: string[], reason?: string) =>
      api.contractControllerUnsign({ selectedContractsIds, reason }),
    markSentByHand: (id: string) => api.contractControllerMarkSentByHand(id),
    markEnteredStatus: (id: string) =>
      api.contractControllerMarkEnteredStatus(id),
    markAsSignedContract: (id: string) =>
      api.contractControllerMarkAsSignedContract(id),
    waitingToSign: (id: string) => api.contractControllerWaitingToSign(id),
    saveSignature: (
      id: string,
      signaturePosition: SignaturePositionSchemaDto,
    ) => api.contractControllerSaveSignature(id, signaturePosition),
    updateAndRemove: (removeContractId: string, updatedContractId: string) =>
      api.contractControllerUpdateAndRemove(
        removeContractId,
        updatedContractId,
      ),
    getContractEmailsByClientId: async (text: string, clientId?: string) => {
      const data = await api.contractControllerGetContractEmailsByClientId(
        text,
        clientId,
      );
      return Array.from(data, value => {
        return {
          value: value,
          label: value,
        };
      });
    },
  };
};

export const newContract = (
  projectId?: string,
  ownerId?: string,
  tv?: boolean,
) => {
  return CreateUpdateContractDtoFromJSON({
    projectId: projectId,
    responsibleOwnerId: ownerId,
    status: ContractStatusEnum.Entered,
    tv: tv,
  });
};

export const validate = (contract: CreateUpdateContractDto) =>
  validateAgainst(ContractSchema, contract);

export const validateToWaitingToSign = (contract: CreateUpdateContractDto) =>
  validateAgainst(ContractToWaitingToSignSchema, contract);

export const convert = (contract?: GetContractExtendedDto | GetContractDto) => {
  if (!contract) {
    return newContract();
  }
  const converted = CreateUpdateContractDtoFromJSON(contract);
  converted.projectId = contract?.project?.id;
  converted.responsibleOwnerId = contract?.responsibleOwner?.id;
  converted.authorizedOwnerId = contract?.authorizedOwner?.id;
  converted.clientId = contract?.client?.id;
  return converted;
};
